import { create } from 'zustand'
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { CHAT_PANEL_DEFAULT_WIDTH, createUiSlice, type UiSlice } from './ui-slice'
import { createStorySlice, type StorySlice } from './story-slice'
import { createBlockSlice, type BlockSlice } from './block-slice'
import { createProjectSlice, type ProjectSlice } from './project-slice'
import { createAuthSlice, type AuthSlice } from './auth-slice'
import { createThemeSlice, type ThemeSlice } from './theme-slice'
import { createDataSlice, type DataSlice } from './data-slice'
import { createDataUiSlice, type DataUiSlice } from './data-ui-slice'
import { createSyncHighlightSlice, type SyncHighlightSlice } from './sync-highlight-slice'
import { createBrainstormSlice, type BrainstormSlice } from './brainstorm-slice'
import { createIdeV2Slice, type IdeV2Slice } from './ide-v2-slice'
import { createExpressionLabSlice, type ExpressionLabSlice } from './expression-lab-slice'
import { createValidationSlice, type ValidationSlice } from './validation-slice'
import { useValidationVerdictStore } from '@/features/validation/verdict-store'
import {
  markPublishReadinessInputsChanged,
  resetPublishReadinessVerdict,
} from '@/features/validation/publish-readiness-verdict'

export type StudioState = UiSlice &
  StorySlice &
  BlockSlice &
  ProjectSlice &
  AuthSlice &
  ThemeSlice &
  DataSlice &
  DataUiSlice &
  SyncHighlightSlice &
  BrainstormSlice &
  IdeV2Slice &
  ExpressionLabSlice &
  ValidationSlice

// Whitelist of UI fields that survive across sessions. Other state
// (project, auth, theme, …) is re-derived on mount, not persisted.
const UI_PERSIST_KEYS: (keyof StudioState)[] = [
  'chatSidebarOpen',
  'inspectorPanelOpen',
  'devtoolsPanelOpen',
  'bottomBarExpanded',
  'navRailExpanded',
  'chatPanelWidth',
  'inspectorPanelWidth',
  'devtoolsPanelHeight',
  // v3 — the preview device the user picked, per project. See
  // `previewDeviceChoices` in ui-slice.ts for why it lives here.
  'previewDeviceChoices',
]
const PRODUCTION_CANARY_BRIDGE_KEY = 'studio.productionCanaryBridge.v1'
const UI_PERSIST_VERSION = 4
const LEGACY_CHAT_PANEL_DEFAULT_WIDTH = 384

/**
 * Migrations are cumulative and each one only ADDS: a payload written by an
 * older Studio must still hydrate every field it did carry. v1 -> v2 repaired
 * the chat width; v2 -> v3 defaults `previewDeviceChoices`, which older blobs
 * predate — without it the map hydrates as `undefined` and the first read
 * throws instead of falling through to the derived default.
 *
 * v3 -> v4 DROPS `navRailExpanded`, and it is the one migration here that
 * discards rather than adds. Every v3 blob carries the field because
 * `partialize` has always written it, but between 984b3135e (2026-07-07,
 * which deleted the `⌘B` writer) and the commit that added the rail's collapse
 * button there was no control on any surface that could set it — so the
 * persisted `false` is the old slice default echoing back, never a choice a
 * user made. Honouring it would collapse the rail of every operator who never
 * asked for that. Dropping the key lets the value fall through to the current
 * default; the first real use of the new control writes a v4 blob that IS
 * intent, and is honoured from then on.
 */
function migratePersistedUiState(persistedState: unknown, version: number): Partial<StudioState> {
  let state = (persistedState ?? {}) as Partial<StudioState>
  if (version < 2 && state.chatPanelWidth === LEGACY_CHAT_PANEL_DEFAULT_WIDTH) {
    state = { ...state, chatPanelWidth: CHAT_PANEL_DEFAULT_WIDTH }
  }
  if (version < 3 && state.previewDeviceChoices === undefined) {
    state = { ...state, previewDeviceChoices: {} }
  }
  if (version < 4 && state.navRailExpanded !== undefined) {
    const { navRailExpanded: _preControlDefault, ...withoutNavRail } = state
    state = withoutNavRail
  }
  return state
}

export const useStudioStore = create<StudioState>()(
  persist(
    subscribeWithSelector(
      immer((set, get, api) => ({
        ...createUiSlice(set as never, get as never, api),
        ...createStorySlice(set as never, get as never, api),
        ...createBlockSlice(set as never, get as never, api),
        ...createProjectSlice(set as never, get as never, api),
        ...createAuthSlice(set as never, get as never, api),
        ...createThemeSlice(set as never, get as never, api),
        ...createDataSlice(set as never, get as never, api),
        ...createDataUiSlice(set as never, get as never, api),
        ...createSyncHighlightSlice(set as never, get as never, api),
        ...createBrainstormSlice(set as never, get as never, api),
        ...createIdeV2Slice(set as never),
        ...createExpressionLabSlice(set as never),
        ...createValidationSlice(set as never, get as never, api),
      })),
    ),
    {
      name: 'cogo-studio-ui-v1',
      version: UI_PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: migratePersistedUiState,
      partialize: (state) => Object.fromEntries(UI_PERSIST_KEYS.map((k) => [k, state[k]])) as Partial<StudioState>,
    },
  ),
)

export function __rehydrateUi(): void {
  const raw = localStorage.getItem('cogo-studio-ui-v1')
  if (!raw) return
  try {
    const parsed = JSON.parse(raw) as {
      state?: Partial<StudioState>
      version?: number
    }
    if (parsed.state) {
      useStudioStore.setState(migratePersistedUiState(parsed.state, parsed.version ?? 0))
    }
  } catch {
    // silent fallback — persist middleware already logs
  }
}

// Phase 378 Plan 02 — expose the studio store on window under VITE_E2E_MOCK
// so Playwright can read currentProject.blocks without hitting the DOM tree.
// Gated by build-time env so the attachment never ships to production bundles.
if (typeof window !== 'undefined' && import.meta.env.VITE_E2E_MOCK === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__COGO_STUDIO__ = useStudioStore
} else if (typeof window !== 'undefined' && shouldExposeProductionCanaryBridge()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__COGO_STUDIO__ = buildProductionCanaryBridge()
}

function shouldExposeProductionCanaryBridge(): boolean {
  try {
    return (
      window.sessionStorage.getItem(PRODUCTION_CANARY_BRIDGE_KEY) === 'enabled' ||
      new URLSearchParams(window.location.search).get('__cogoStudioCanaryBridge') === '1'
    )
  } catch {
    return false
  }
}

function buildProductionCanaryBridge() {
  return {
    getState: () => {
      const state = useStudioStore.getState()
      return {
        currentProject: state.currentProject,
        currentProjectId: state.currentProjectId,
        currentPageId: state.currentPageId,
        activeRailItem: state.activeRailItem,
        viewMode: state.viewMode,
        setActiveRailItem: state.setActiveRailItem,
        setChatSidebarOpen: state.setChatSidebarOpen,
        applyEndUserBrowserEvidence: state.applyEndUserBrowserEvidence,
        persistCurrentPublishEvidence: state.persistCurrentPublishEvidence,
        updateAppDesignColor: state.updateAppDesignColor,
        setAppDesignRadiusScale: state.setAppDesignRadiusScale,
        setAppDesignFontFamily: state.setAppDesignFontFamily,
      }
    },
  }
}

// Auto-sync chatContext.mode when workspace mode changes
useStudioStore.subscribe(
  (s) => s.mode,
  (mode) => {
    useStudioStore.getState().setChatContext({
      ...useStudioStore.getState().chatContext,
      mode,
    })
  },
)

// Auto-sync chatContext.flowId when flow selection changes
import('./flow-context-sync').catch(() => {})

// Cross-tab selection synchronization replaced by MCP notification bus (212-02)

// Reset story when project changes (so story-content-canvas regenerates from new manifest)
useStudioStore.subscribe(
  (s) => s.currentProjectId,
  () => {
    useStudioStore.getState().setStory(null)
  },
)

// Reset validation when project changes (validationReport is project-scoped).
// The shared verdict store is cleared too: every published verdict belongs to
// the previous project, and a leftover entry (esp. kg-shacl, which only
// republishes when the DS-surface validator next runs) would otherwise be
// presented as fresh app health for the new project.
useStudioStore.subscribe(
  (s) => s.currentProjectId,
  () => {
    useStudioStore.getState().clearValidation()
    useValidationVerdictStore.getState().clearAll()
    // The publish-readiness publisher memoizes its last-published signature to
    // avoid an effect loop; the store was just wiped, so reset the memo or the
    // new project's pre-flight could be suppressed by a stale signature (F25).
    resetPublishReadinessVerdict()
  },
)

// Any project content mutation invalidates the last whole-app KG SHACL
// verdict. Marking staleness here (not in the DS-surface ValidatorPanel
// effect) keeps the freshness cue honest while that panel is unmounted —
// e.g. edits made from the Code surface. markStale is a no-op when kg-shacl
// has not published (including right after the project-switch clearAll).
useStudioStore.subscribe(
  (s) => s.currentProject,
  (curr, prev) => {
    if (!curr || curr === prev) return
    const store = useValidationVerdictStore.getState()
    store.markStale('kg-shacl')
    // The publish-readiness pre-flight is computed from the project content, so
    // a content edit makes the last estimate stale too — otherwise a fresh
    // green pre-flight would survive an edit that could break publish (F25).
    store.markStale('publish-readiness')
    // A content edit is a GENUINE change to the publish-readiness inputs, so it
    // un-sticks any authoritative server 'fix' — the next surface estimate is
    // then allowed to move the pill (CA-1 = L5-C4). Without this, the server
    // 'fix' would stick forever; with it, only a real edit (not a bare remount)
    // can re-green.
    markPublishReadinessInputsChanged()
  },
)

// Initialize auto-save bridge (connects editor stores to Supabase persistence).
// A deferred retry keeps ESM circular loading from dropping the bridge during
// startup/HMR while studio-store and auto-save-bridge import each other.
function initAutoSaveBridgeWhenReady(retries = 3): void {
  void import('./auto-save-bridge')
    .then((m) => {
      if (typeof m.initAutoSaveBridge === 'function') {
        m.initAutoSaveBridge(useStudioStore)
        return
      }
      if (retries > 0) {
        setTimeout(() => initAutoSaveBridgeWhenReady(retries - 1), 0)
      }
    })
    .catch(() => {
      if (retries > 0) {
        setTimeout(() => initAutoSaveBridgeWhenReady(retries - 1), 0)
      }
    })
}

initAutoSaveBridgeWhenReady()
